import React from 'react';
import { FileQuestion, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import styles from './DownloadRenderer.module.css';
import { useState } from 'react';

const cardVariants = {
  hidden: { opacity: 0, y: 14, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1],
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
  },
};

const UnsupportedRenderer = ({ contentDetails, viewerUrl }) => {

  const [isActiveApiCall, setIsActiveApiCall] = useState(false);
  
  const handleDownload = async () => {
    if (!viewerUrl) return;

    setIsActiveApiCall(true)
    try {
      const response = await fetch(viewerUrl);

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = contentDetails?.title || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    } finally{
      setIsActiveApiCall(false)
      
    }
  };
  
  const fileName = contentDetails?.metadata?.file_metadata?.name || 'Unknown file';
  const fileType = contentDetails?.file_type || 'unknown format';
  const fileSize = contentDetails?.metadata?.file_metadata?.size;
  
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return 'Unknown size';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={styles.container}>
      <motion.div
        className={styles.card}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className={styles.iconContainer}
          variants={itemVariants}
          whileHover={{ scale: 1.06, rotate: -2 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        >
          <FileQuestion size={48} className={styles.icon} />
        </motion.div>
        
        <motion.h2 className={styles.title} variants={itemVariants}>
          Preview Unavailable
        </motion.h2>
        
        <motion.p className={styles.description} variants={itemVariants}>
          This file type ({fileType}) cannot be previewed in the browser. You can download it to view its contents.
        </motion.p>
        
        <motion.div className={styles.fileInfo} variants={itemVariants}>
          <span className={styles.fileName}>{fileName}</span>
          {fileSize && (
            <span className={styles.fileSize}>({formatBytes(fileSize)})</span>
          )}
        </motion.div>
        
        {/* Protected Download CTA Button */}
        <motion.button 
          className={styles.downloadButton}
          variants={itemVariants}
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98, y: 0 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          disabled={isActiveApiCall}
          onClick={handleDownload}
        >
          <motion.span
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            whileHover={{ y: 1 }}
            transition={{ duration: 0.2 }}
          >
            <Download size={18} />
            <span>Download File</span>
          </motion.span>
        </motion.button>
      </motion.div>
    </div>
  );
};

export default UnsupportedRenderer;